import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectHasProposedData, selectNotesReason, selectOldProposalData, setHasProposedData, setNotesReason, setOldProposalData, } from '../../../../features/actuarial/request-amendment/request-amendment.slice';
import { setUpdateTypeModal } from '../../../../features/gms-state-and-modal/modal-slice';
import { useCreateProposalForm } from '../../../../hooks/actions/use-create-proposal-form';
import { useProposalData } from '../../../../hooks/populate-data/use-proposal-data';

interface Props {
    onCancel: () => void;
    onConfirm: () => void;
    modalTitle: string;
    modalSubTitle: string;
    confirmTitle?: string;
    onCloseTitle?: string;
    onUpdateProposal?: () => void;
    onProposalUpdated?: () => void;
    isLoading: boolean;
    type: 'request' | 'decline' | 'accept'
}

export default function NotesAmendmentConfirmation({ type, isLoading, onCloseTitle = 'Close', confirmTitle = 'Continue Request', onCancel, onConfirm, modalTitle, modalSubTitle, onUpdateProposal, onProposalUpdated, }: Props) {
    const dispatch = useDispatch();

    const reasonNotes = useSelector(selectNotesReason);
    const includeProposalUpdate = useSelector(selectHasProposedData);
    const { proposalData } = useCreateProposalForm()
    const originalProposal = useSelector(selectOldProposalData);

    const handleToggleProposalUpdate = () => {
        dispatch(setHasProposedData(!includeProposalUpdate))
    };

    const {
        repopulateAll
    } = useProposalData()

    const handleUpdateProposal = () => {
        // setOriginalProposal(proposalData)
        repopulateAll()
        dispatch(setUpdateTypeModal('updateProposal'));
        onUpdateProposal?.();
        dispatch(setOldProposalData(structuredClone(proposalData)));
    };

    const handleConfirm = () => {
        if (!reasonNotes?.trim()) {
            return;
        }

        onConfirm();
    };


    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
                <div className="flex max-h-[90dvh] w-full max-w-[620px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                    {/* Header */}
                    <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14" />
                                <path d="M8 8h8" />
                                <path d="M8 12h5" />
                            </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-sm font-semibold text-gray-900">
                                {modalTitle}
                            </h2>
                            <p className="mt-0.5 text-xs text-gray-500">
                                {modalSubTitle}
                            </p>
                        </div>
                    </div>
                    {/* Content */}
                    <div className="min-h-0 overflow-y-auto p-4 sm:p-5">
                        {/* Amendment Notes */}
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <svg className="h-4 w-4 shrink-0 text-[#186cb2]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                    <path d="M4 5h16" />
                                    <path d="M4 9h16" />
                                    <path d="M4 13h10" />
                                    <path d="M4 17h7" />
                                </svg>
                                <label className="text-xs font-semibold text-gray-700">
                                    Amendment Notes
                                </label>
                                <span className="text-[10px] text-red-500">
                                    Required
                                </span>
                            </div>
                            <textarea
                                disabled={isLoading}
                                rows={6}
                                value={reasonNotes}
                                onChange={(e) => dispatch(setNotesReason(e.target.value))}
                                placeholder="Describe the changes or revisions required..."
                                className="w-full resize-y rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-700 placeholder:text-gray-400 focus:border-[#186cb2] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#186cb2]/10"
                            />
                            <div className="mt-1.5 flex items-center justify-between gap-3">
                                <p className="text-[10px] text-gray-400">
                                    Provide enough detail for the Actuarial team to review.
                                </p>
                                <span className="shrink-0 text-[10px] text-gray-400">
                                    {reasonNotes?.length ?? 0} characters
                                </span>
                            </div>
                        </div>
                        {type === 'request' && (
                            <>
                                {/* Updated Proposal */}
                                <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50/70 p-3">
                                    <div className="flex items-start gap-3">
                                        {/* Icon */}
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#186cb2]/10 text-[#186cb2]">
                                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" >
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <path d="M14 2v6h6" />
                                                <path d="M8 13h8" />
                                                <path d="M8 17h5" />
                                            </svg>
                                        </div>
                                        {/* Label */}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="text-xs font-semibold text-gray-700">
                                                    Include Updated Proposal
                                                </p>
                                                <span className="text-[10px] text-gray-400">
                                                    Optional
                                                </span>
                                            </div>
                                            <p className="mt-0.5 text-[10px] leading-relaxed text-gray-400">
                                                Include an updated proposal for Actuarial to review.
                                            </p>
                                        </div>
                                        {/* Switch */}
                                        <button
                                            disabled={isLoading}
                                            type="button"
                                            role="switch"
                                            aria-checked={includeProposalUpdate}
                                            aria-label="Include Updated Proposal"
                                            onClick={handleToggleProposalUpdate}
                                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                                                includeProposalUpdate ? 'bg-[#186cb2]' : 'bg-gray-300' }
                                            `}
                                        >
                                            <span
                                                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                                                    includeProposalUpdate ? 'translate-x-4' : 'translate-x-1' }
                                                `}
                                            />
                                        </button>
                                    </div>

                                    {/* Proposal Actions */}
                                    {includeProposalUpdate && (
                                        <div className="mt-3">
                                            <div className="grid grid-cols-1 gap-2">
                                                <button
                                                    disabled={isLoading}
                                                    type="button"
                                                    onClick={handleUpdateProposal}
                                                    className="flex items-center justify-center gap-2 rounded-md border border-[#186cb2] bg-white px-3 py-2 text-xs font-medium text-[#186cb2] transition hover:bg-[#eef6fc] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    <>
                                                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M12 20h9" />
                                                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 4H3v-4Z" />
                                                        </svg>
                                                        Edit Proposal
                                                    </>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    {/* Footer */}
                    <div className="grid grid-cols-2 gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-5">
                        <button
                            disabled={isLoading}
                            type="button"
                            onClick={onCancel}
                            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {onCloseTitle}
                        </button>

                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={!reasonNotes.trim() || isLoading}
                            className="flex items-center justify-center gap-2 rounded-md bg-[#186cb2] px-3 py-2 text-xs font-medium text-white transition hover:bg-[#145d9b] disabled:cursor-not-allowed disabled:bg-gray-300"
                        >
                            {isLoading ? (
                                <>
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                    <span>Processing...</span>
                                </>
                            ) : (
                                confirmTitle
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}